if (!customElements.get('onest-home-slideshow')) {
  customElements.define('onest-home-slideshow', class extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.abort = new AbortController();
      this.slides = [...this.querySelectorAll('[data-home-slide]')];
      this.dots = [...this.querySelectorAll('[data-home-index]')];
      this.stage = this.querySelector('[data-home-stage]');
      this.transitionAnimations = [];
      this.index = 0;
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.paused = this.dataset.autoplay !== 'true' || this.motion.matches;
      this.generation = 0;
      const options = { signal: this.abort.signal };
      this.querySelector('[data-home-controls]').hidden = false;
      this.addEventListener('click', event => {
        const button = event.target.closest('button');
        if (!button) return;
        if (button.hasAttribute('data-home-index')) this.show(Number(button.dataset.homeIndex), true);
        if (button.hasAttribute('data-home-pause')) {
          this.paused = !this.paused;
          this.syncPause();
          this.schedule();
        }
      }, options);
      this.addEventListener('mouseenter', () => this.schedule(), options);
      this.addEventListener('mouseleave', () => this.schedule(), options);
      this.addEventListener('focusin', () => this.schedule(), options);
      this.addEventListener('focusout', () => queueMicrotask(() => this.schedule()), options);
      this.addEventListener('pointerdown', event => { this.swiped = false; this.startX = event.clientX; this.startY = event.clientY; }, options);
      this.addEventListener('pointerup', event => {
        if (event.pointerType !== 'touch' || this.startX == null) return;
        const dx = event.clientX - this.startX;
        const dy = event.clientY - this.startY;
        this.startX = null;
        if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) {
          this.swiped = true;
          this.show(this.index + (dx < 0 ? 1 : -1), true);
        }
      }, options);
      this.addEventListener('click', event => {
        if (this.swiped && event.target.closest('a')) event.preventDefault();
        this.swiped = false;
      }, { ...options, capture: true });
      this.addEventListener('dragstart', event => event.preventDefault(), options);
      document.addEventListener('visibilitychange', () => this.schedule(), options);
      this.motion.addEventListener('change', () => {
        if (this.motion.matches) { this.paused = true; this.syncPause(); this.finishTransition(); }
        this.schedule();
      }, options);
      this.observer = new IntersectionObserver(() => this.schedule(), { threshold: 0.15 });
      this.observer.observe(this);
      this.syncPause();
      this.schedule();
    }
    disconnectedCallback() {
      this.generation++;
      clearTimeout(this.timer);
      this.finishTransition();
      this.observer?.disconnect();
      this.abort?.abort();
      this.abort = null;
    }
    syncPause() {
      const button = this.querySelector('[data-home-pause]');
      button.setAttribute('aria-label', this.paused ? this.dataset.play : this.dataset.pause);
      button.querySelector('[data-home-pause-icon]').textContent = this.paused ? '▶' : 'Ⅱ';
    }
    schedule() {
      clearTimeout(this.timer);
      if (!this.canRotate()) return;
      this.timer = setTimeout(() => { if (this.canRotate()) this.show(this.index + 1); }, Number(this.dataset.interval) || 4000);
    }
    canRotate() {
      if (!this.isConnected || this.paused || document.hidden || this.contains(document.activeElement)) return false;
      if (matchMedia('(hover: hover)').matches && this.matches(':hover')) return false;
      const bounds = this.getBoundingClientRect();
      return bounds.bottom > 0 && bounds.top < window.innerHeight;
    }
    async show(index, manual = false) {
      const next = (index + this.slides.length) % this.slides.length;
      const generation = ++this.generation;
      clearTimeout(this.timer);
      if (next === this.index) { this.finishTransition(); this.schedule(); return; }
      await Promise.all([...this.slides[next].querySelectorAll('img')].map(image => {
        image.loading = 'eager';
        return image.decode?.().catch(() => {});
      }));
      if (!this.isConnected || generation !== this.generation) return;
      const previous = this.slides[this.index];
      const previousHeight = this.stage.getBoundingClientRect().height;
      const direction = index > this.index ? 1 : -1;
      this.finishTransition();
      this.index = next;
      this.slides.forEach((slide, at) => {
        slide.classList.toggle('is-active', at === next);
        slide.toggleAttribute('inert', at !== next);
        slide.setAttribute('aria-hidden', String(at !== next));
        this.dots[at].setAttribute('aria-current', String(at === next));
      });
      if (manual) this.querySelector('[data-home-status]').textContent = this.slides[next].getAttribute('aria-label');
      if (!this.motion.matches) {
        const nextHeight = this.stage.getBoundingClientRect().height;
        const timing = { duration: 600, easing: 'ease', fill: 'both' };
        previous.classList.add('is-leaving');
        this.transitionAnimations = [
          previous.animate([{ transform: 'translateX(0)' }, { transform: `translateX(${-direction * 100}%)` }], timing),
          this.slides[next].animate([{ transform: `translateX(${direction * 100}%)` }, { transform: 'translateX(0)' }], timing)
        ];
        if (previousHeight > 0 && nextHeight > 0 && Math.abs(previousHeight - nextHeight) > 1) {
          this.transitionAnimations.push(this.stage.animate([{ height: `${previousHeight}px` }, { height: `${nextHeight}px` }], timing));
        }
      }
      this.schedule();
      await Promise.allSettled(this.transitionAnimations.map(animation => animation.finished));
      if (generation === this.generation) this.finishTransition();
    }
    finishTransition() {
      this.transitionAnimations?.forEach(animation => animation.cancel());
      this.transitionAnimations = [];
      this.slides?.forEach(slide => slide.classList.remove('is-leaving'));
    }
  });
}
